/**
 * Accès au domaine documents.
 *
 * Le fichier ne transite jamais par notre API : `preparerDepot` obtient une URL S3 pré-signée, le
 * navigateur y envoie l'octet directement, puis `confirmer` fait exister le document
 * (`docs/agent-spec/05-api-backend.md`). Faire passer le fichier par Lambda buterait sur la limite de
 * charge utile d'API Gateway, en plus de facturer du temps d'exécution pour du simple transfert.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, requete } from "../../shared/api/client";
import type { DemandeDepot, Document } from "../../shared/api/types";

export type FiltresDocuments = {
  numeroGrv?: string;
  typeDocument?: string;
};

export function useDocumentsTransversal(filtres: FiltresDocuments) {
  return useQuery({
    queryKey: ["documents", "transversal", filtres],
    queryFn: () =>
      api.lire<Document[]>(
        "/v1/documents" +
          requete({
            "filter[numero_grv]": filtres.numeroGrv,
            "filter[type_document]": filtres.typeDocument,
            // Vue transversale sans pagination à l'écran (`EcranDocuments.tsx`, tri/recherche
            // côté client) : la taille de page doit donc couvrir la flotte cible entière (500 GRV,
            // `docs/agent-spec/00-README.md`), avec plusieurs documents par GRV — pas seulement une
            // page de 50, sans quoi une partie des documents disparaîtrait silencieusement de la
            // liste et de son export.
            pageSize: 2000,
          }),
      ),
  });
}

type ReponseDepot = { document_id: string; url_depot: string };
type ReponseTelechargement = { document_id: string; nom_fichier: string; url_telechargement: string };

export function useDocuments(numero: string) {
  return useQuery({
    queryKey: ["documents", numero],
    queryFn: () => api.lire<Document[]>(`/v1/grv/${encodeURIComponent(numero)}/documents?pageSize=50`),
  });
}

export function useDeposerDocument(numero: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ demande, fichier }: { demande: DemandeDepot; fichier: File }) => {
      const depot = await api.creer<ReponseDepot>(
        `/v1/grv/${encodeURIComponent(numero)}/documents`,
        demande,
      );
      // Envoi direct au bucket, sans passer par notre client d'API : ce n'est pas un appel à notre
      // domaine, l'URL pré-signée porte sa propre autorisation.
      const envoi = await fetch(depot.data.url_depot, {
        method: "PUT",
        headers: { "Content-Type": demande.content_type ?? "application/octet-stream" },
        body: fichier,
      });
      if (!envoi.ok) {
        throw new Error("L'envoi du fichier a échoué. Vérifier la connexion puis réessayer.");
      }
      return api.modifier(`/v1/documents/${encodeURIComponent(depot.data.document_id)}/confirm`);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["documents", numero] });
    },
  });
}

export function useUrlDeTelechargement() {
  return useMutation({
    mutationFn: (documentId: string) =>
      api.lire<ReponseTelechargement>(`/v1/documents/${encodeURIComponent(documentId)}/download`),
  });
}

export function useSupprimerDocument(numero: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.supprimer(`/v1/documents/${encodeURIComponent(documentId)}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["documents", numero] });
      void client.invalidateQueries({ queryKey: ["documents", "transversal"] });
    },
  });
}
